/**
 * "Is there still a session?", asked of every query rather than just the gate's.
 *
 * The gate mounts the console while `/api/admin/me` is still in flight, so the
 * first read to discover an absent session is often a PAGE query, not the
 * gate's. Without this the console would answer that with a page-level error
 * state — a screen full of failures and no way to sign in.
 *
 * Only a 401. A 403 is a real answer about a real caller ("you are not an
 * admin") and a login can never help them; a 500 is a broken read, and hiding
 * it behind a sign-in screen would hide the failure too.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { ApiError } from "./api";

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/** True while ANY query in the cache is holding a 401. */
export function useAnyQueryUnauthorized(): boolean {
  const cache = useQueryClient().getQueryCache();
  // Subscribed rather than polled: the answer has to change the render that is
  // already on screen, and a boolean snapshot is stable enough for
  // useSyncExternalStore to compare by value.
  const read = () =>
    cache.getAll().some((query) => isUnauthorized(query.state.error));
  return useSyncExternalStore((notify) => cache.subscribe(notify), read, read);
}
